import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import mayfairLogo from '../../logo.svg';
import api from '../../services/api';

export default function FirstLoginPassword() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');

        if (password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }
        if (new TextEncoder().encode(password).length > 72) {
            setError('Password must be no more than 72 bytes.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        const passwordSetupToken = sessionStorage.getItem('passwordSetupToken');
        if (!passwordSetupToken) {
            setError('Your password setup session has expired. Please sign in again.');
            return;
        }

        setLoading(true);
        try {
            await api.post('/auth/first-login-password', {
                passwordSetupToken,
                temporaryPassword: location.state?.temporaryPassword,
                newPassword: password,
                confirmPassword
            });
            sessionStorage.removeItem('passwordSetupToken');
            setSuccess(true);
        } catch (err) {
            if (err.response?.status === 401) {
                sessionStorage.removeItem('passwordSetupToken');
            }
            setError(err.response?.data?.error || 'Could not create your password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] shadow-2xl shadow-[#3a663f]/25 p-3">
                        <img src={mayfairLogo} alt="Mayfair Insurance" className="w-full h-full object-contain" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mt-4">Create Your Password</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Set a new password to finish signing in.
                    </p>
                </div>

                <div className="bg-white rounded-3xl shadow-2xl border border-gray-100/80 p-8 md:p-10">
                    {success ? (
                        <div className="space-y-5 text-center">
                            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-2xl">
                                Password created. Sign in using your new password.
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate('/login', { replace: true })}
                                className="w-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] text-white font-semibold py-3 rounded-2xl shadow-lg"
                            >
                                Continue to Sign In
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <p className="text-sm text-gray-600">
                                Your administrator provided a temporary password. Choose a new password before continuing.
                            </p>
                            {!location.state?.temporaryPassword && (
                                <div role="alert" className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-2xl">
                                    Your sign-in session is missing. Return to sign in with your temporary password to continue.
                                </div>
                            )}
                            {error && (
                                <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl">
                                    {error}
                                </div>
                            )}
                            <div className="space-y-2">
                                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">New password</label>
                                <input
                                    id="new-password"
                                    type="password"
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(event) => setPassword(event.target.value)}
                                    minLength={6}
                                    required
                                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none"
                                />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">Confirm new password</label>
                                <input
                                    id="confirm-password"
                                    type="password"
                                    autoComplete="new-password"
                                    value={confirmPassword}
                                    onChange={(event) => setConfirmPassword(event.target.value)}
                                    minLength={6}
                                    required
                                    className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none"
                                />
                            </div>
                            <p className="text-xs text-gray-500">Use at least 6 characters.</p>
                            <button
                                type="submit"
                                disabled={loading || !location.state?.temporaryPassword}
                                className="w-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-3 rounded-2xl shadow-lg disabled:opacity-60"
                            >
                                {loading ? 'Saving password...' : 'Save New Password'}
                            </button>
                            <div className="text-center">
                                <Link to="/login" onClick={() => sessionStorage.removeItem('passwordSetupToken')} className="text-sm text-[#2d5232] hover:underline">
                                    Back to sign in
                                </Link>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
